import { useEffect, useState } from 'react';
import { Alert, Drawer, Spin } from '@arco-design/web-react';
import { errorMessage } from '../../../common/api/httpClient';
import type {
  PortalApplication,
  PortalIdentity,
} from '../../../model/portalModels';
import { operationsApi } from '../api/operationsApi';
import { mapOperationLists } from '../model/operationModels';
import type {
  PortalOperationPageResponse,
} from '../types/portal.types';
import PortalOperationListView from '../views/PortalOperationListView';
import { PortalOperationHost } from '../components/PortalOperationHost';

const OPERATION_PAGE_SIZE = 20;

const EMPTY_PAGE: PortalOperationPageResponse = {
  total: 0,
  page: 1,
  pageSize: OPERATION_PAGE_SIZE,
  favoriteVersion: 0,
  groupFilters: [],
  groups: [],
};

export function PortalOperationListPage(props: {
  applications: PortalApplication[];
  identity: PortalIdentity;
  applicationCode?: string;
  operationCode?: string;
  onNavigate: (path: string) => void;
  onSessionExpired: () => void;
}) {
  const [pageData, setPageData] =
    useState<PortalOperationPageResponse>(EMPTY_PAGE);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [groupCode, setGroupCode] =
    useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    void operationsApi.list({
      page,
      pageSize: OPERATION_PAGE_SIZE,
      keyword,
      groupCode,
    }).then(result => {
      if (active) setPageData(result);
    }).catch(requestError => {
      if (active) setError(errorMessage(requestError));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [groupCode, keyword, page]);

  return (
    <>
      {error ? <Alert type="error" content={error} /> : null}
      <Spin loading={loading} tip="正在加载操作列表...">
        <PortalOperationListView
          operationLists={mapOperationLists(pageData)}
          groupFilters={pageData.groupFilters}
          keyword={keyword}
          activeGroupCode={groupCode}
          page={pageData.page}
          pageSize={pageData.pageSize}
          total={pageData.total}
          onSearch={nextKeyword => {
            setKeyword(nextKeyword.trim());
            setPage(1);
          }}
          onGroupChange={nextGroupCode => {
            setGroupCode(nextGroupCode);
            setPage(1);
          }}
          onPageChange={setPage}
          onNavigate={props.onNavigate}
        />
      </Spin>
      <Drawer
        className="portal-operation-drawer"
        title="快捷操作"
        width="min(1100px, calc(100vw - 88px))"
        visible={Boolean(
          props.applicationCode && props.operationCode,
        )}
        footer={null}
        onCancel={() => props.onNavigate('/portal/operations')}
      >
        {props.applicationCode && props.operationCode ? (
          <PortalOperationHost
            applicationCode={props.applicationCode}
            operationCode={props.operationCode}
            applications={props.applications}
            identity={props.identity}
            onSessionExpired={props.onSessionExpired}
            onClose={() =>
              props.onNavigate('/portal/operations')}
          />
        ) : null}
      </Drawer>
    </>
  );
}
